/**
 * WishlistStar Component
 * A reusable star icon for adding/removing items from the wishlist
 * Outline when not in wishlist, filled solid when in wishlist
 */

import React, { useState, useEffect } from 'react';
import { Star, Loader2 } from 'lucide-react';
import { addToWishlist, checkWishlistStatus, removeFromWishlist } from './WishlistModal';

const WishlistStar = ({ 
  itemType, // 'track', 'album', 'event', 'gear', 'venue'
  itemData, // object containing item details (must have 'id')
  size = 18,
  className = ''
}) => {
  const [inWishlist, setInWishlist] = useState(false);
  const [wishlistItemId, setWishlistItemId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!itemData?.id) return;
    
    const checkStatus = async () => {
      const status = await checkWishlistStatus(itemType, itemData.id);
      setInWishlist(status.in_wishlist);
      setWishlistItemId(status.wishlist_item_id);
    };
    checkStatus();
  }, [itemType, itemData?.id]);

  const handleToggle = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!itemData?.id) return;
    
    setLoading(true);
    
    try {
      if (inWishlist && wishlistItemId) {
        const success = await removeFromWishlist(wishlistItemId);
        if (success) {
          setInWishlist(false);
          setWishlistItemId(null);
        }
      } else {
        const success = await addToWishlist(itemType, itemData);
        if (success) {
          setInWishlist(true);
          const status = await checkWishlistStatus(itemType, itemData.id);
          setWishlistItemId(status.wishlist_item_id);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleToggle}
      disabled={loading}
      className={`wishlist-star-btn ${className}`}
      style={{
        background: 'transparent',
        border: 'none',
        cursor: loading ? 'wait' : 'pointer',
        padding: '4px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: loading ? 0.5 : 1,
        transition: 'transform 0.2s ease',
        verticalAlign: 'middle'
      }}
      onMouseEnter={(e) => !loading && (e.currentTarget.style.transform = 'scale(1.2)')}
      onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
      title={inWishlist ? 'Remove from Wishlist' : 'Add to Wishlist'}
    >
      {loading ? (
        <Loader2 
          size={size} 
          style={{ color: '#ffd700' }} 
          className="animate-spin" 
        />
      ) : (
        <Star 
          size={size} 
          style={{ 
            color: inWishlist ? '#ffd700' : '#888',
            fill: inWishlist ? '#ffd700' : 'transparent',
            transition: 'all 0.2s ease'
          }} 
        />
      )}
    </button>
  );
};

export default WishlistStar;
